import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Avatar,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { CalendarIcon } from "@mui/x-date-pickers";
import { Circle, BookmarkBorder } from "@mui/icons-material";
import GroupsIcon from "@mui/icons-material/Groups";
import { GradientButton } from "../../../../RegisterTalentForm/RegisterTalentForm";
import TimeDuration from "../../../../Profile/TalentInfo/TimeDuration";
import { useAuth } from "../../../../../hooks";
import BookingModal from "./BookingModal";
import { CardDetailBanner } from "../TalentCardDesign/TalentCardDetailPage/CardDetailBanner_updated";
import { CardFormValues } from "../../../../../types/Card";
import { enqueueSnackbar } from "notistack";
import { useGetStageCard } from "../../../../../services/stagemode_api/talent/talent_api";
import { useCurrentTalentProfile } from "../../../../../hooks";

import dayjs from "dayjs";
import GuestSignInModal from "../../../../../pages/Auth/GuestSignInModal";
import GuestLiteModal from "../../../../../pages/Auth/GuestLiteModal";
import { Icons } from "../../../../../components/assets/icon";
import useCurrentBusinessInfo from "../../../../../hooks/useCurrentBusiness";

const FeaturedEventDetail: React.FC = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const purchasedChips = userInfo?.wallet_purchase?.available_chips || 0;
  const earnedChips = userInfo?.wallet_earning?.available_chips || 0;
  const { currentProfile } = useCurrentTalentProfile();
  const { currentBusiness } = useCurrentBusinessInfo();
  const { data, isLoading } = useGetStageCard(id ?? "");
  const eventData = data?.data ?? {};
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const isCardCreator = eventData?.talent?.id === (currentProfile?.id ?? null);
  const isBusinessCardCreator =
    eventData?.business?.id === (currentBusiness?.id ?? null);

  console.log(eventData?.talent?.id, "eventData");
  console.log(currentProfile?.id, "currentProfile");
  console.log(isCardCreator, "isCardCreator");

  // for guest login modal
  const isGuest = userInfo ? false : true;
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);

  const isGuestLite = userInfo?.profile_completed === false;
  const [isGuestLiteModalOpen, setIsGuestLiteModalOpen] = useState(false);

  const handleBookingSuccess = () => {
    setIsBooked(true);
    setIsBookingModalOpen(false);
    enqueueSnackbar("Booking successful", { variant: "success" });
  };

  const getEmbeddedVideoUrl = (url: string) => {
    if (url.includes("youtube.com/watch")) {
      return url.replace("watch?v=", "embed/");
    } else if (url.includes("youtu.be/")) {
      return url.replace("youtu.be/", "youtube.com/embed/");
    } else if (url.includes("vimeo.com")) {
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  if (isLoading) return <Box sx={{ color: "#fff", p: 3 }}>Loading...</Box>;

  // —— Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.78)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
    chipBg: "rgba(255,255,255,0.06)",
    chipBorder: "rgba(255,255,255,0.18)",
    gold: "#F59E0B",
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: "calc(100vw - 32px)", md: 1080 },
        mx: "auto",
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 4 },
        fontFamily: "Lexend",
        background: COLORS.bg,
        color: COLORS.text,
        position: "relative",
        borderRadius: { xs: 0, md: 3 },
        // subtle vignette
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 480px at 15% 0%, rgba(229,9,20,.08), transparent 60%)",
          pointerEvents: "none",
          borderRadius: { xs: 0, md: 12 },
        },
      }}
    >
      {/* Banner Section */}
      <Box position="relative" mb={4} borderRadius={3} overflow="hidden">
        <CardDetailBanner
          primary_thumbnail={eventData?.primary_thumbnail ?? ""}
          suit="Clover"
          card={eventData as unknown as CardFormValues}
          title={
            eventData?.banner_title || eventData?.title || "Featured Event"
          }
        />

        {/* dark glassy action */}
        <Tooltip title="Add to Favorites">
          <IconButton
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              border: `1px solid ${COLORS.border}`,
              backgroundColor: "rgba(0,0,0,0.55)",
              borderRadius: 2,
              zIndex: 10,
              color: COLORS.text,
              backdropFilter: "blur(6px)",
              "&:hover": {
                backgroundColor: COLORS.red,
                borderColor: COLORS.red,
              },
            }}
          >
            <BookmarkBorder />
          </IconButton>
        </Tooltip>

        {/* bottom fade for readability over banner */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: { xs: 48, md: 64 },
            background:
              "linear-gradient(180deg, rgba(11,11,11,0) 0%, rgba(11,11,11,.85) 100%)",
          }}
        />
      </Box>

      {/* Title & Booking */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Typography
          variant="h5"
          fontWeight={900}
          sx={{
            letterSpacing: "-0.01em",
            color: COLORS.text,
          }}
        >
          {eventData?.title || "No title available"}
        </Typography>

        {!isCardCreator && !isBusinessCardCreator && (
          <GradientButton
            onClick={() =>
              !isGuest
                ? isGuestLite
                  ? setIsGuestLiteModalOpen(true)
                  : setIsBookingModalOpen(true)
                : setIsGuestModalOpen(true)
            }
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 900,
              textTransform: "none",
              background: `${COLORS.red} !important`,
              color: "#fff !important",
              boxShadow: "0 10px 24px rgba(229,9,20,0.35) !important",
              "&:hover": { background: `${COLORS.redHover} !important` },
            }}
          >
            Book Now
          </GradientButton>
        )}

        <GuestSignInModal
          open={isGuestModalOpen}
          onClose={() => setIsGuestModalOpen(false)}
        />
      </Stack>

      {/* Details: chips */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={3}>
        {eventData?.start_date_time && (
          <Tooltip title="Event Date & Time">
            <Chip
              icon={<CalendarIcon sx={{ color: COLORS.muted, fontSize: 20 }} />}
              label={dayjs(eventData.start_date_time).format(
                "ddd, D MMM YYYY, h:mm a"
              )}
              sx={{
                background: COLORS.chipBg,
                border: `1px solid ${COLORS.chipBorder}`,
                color: COLORS.text,
                fontWeight: 600,
              }}
            />
          </Tooltip>
        )}
        <Box
          sx={{
            "& .MuiChip-root": {
              background: COLORS.chipBg,
              border: `1px solid ${COLORS.chipBorder}`,
              color: COLORS.text,
              fontWeight: 600,
            },
          }}
        >
          <TimeDuration duration={eventData?.duration ?? 0} />
        </Box>
        {!!eventData?.venue_size && (
          <Tooltip title="Venue Size">
            <Chip
              icon={<GroupsIcon sx={{ color: COLORS.muted, fontSize: 20 }} />}
              label={eventData.venue_size}
              sx={{
                background: COLORS.chipBg,
                border: `1px solid ${COLORS.chipBorder}`,
                color: COLORS.text,
                fontWeight: 600,
              }}
            />
          </Tooltip>
        )}
        {!!eventData?.price && (
          <Tooltip title="Session Price">
            <Chip
              icon={<Icons.Coin height={20} width={20} />}
              label={eventData?.price > 0 ? ` ${eventData.price}` : "Free"}
              sx={{
                background: "rgba(229,9,20,0.12)",
                border: `1px solid ${COLORS.red}`,
                color: COLORS.text,
                fontWeight: 700,
              }}
            />
          </Tooltip>
        )}
      </Stack>

      {/* Talent Info */}
      {eventData?.talent && (
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Avatar
            src={eventData?.talent?.profile_image}
            onClick={() => navigate(`/talent/profile/${eventData?.talent?.id}`)}
            sx={{ cursor: "pointer" }}
          />
          <Box>
            <Typography
              fontWeight={700}
              onClick={() =>
                navigate(`/talent/profile/${eventData?.talent?.id}`)
              }
              sx={{ cursor: "pointer", color: COLORS.text }}
            >
              {eventData?.talent?.username}
            </Typography>
            <Typography fontSize={13} sx={{ color: COLORS.muted }}>
              {eventData?.talent?.bio}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* Short description */}
      <Typography
        variant="body1"
        mb={4}
        sx={{ lineHeight: 1.7, color: COLORS.muted }}
      >
        {eventData?.short_description || "No description available"}
      </Typography>

      {/* About Event */}
      <Box mb={4}>
        <Typography
          variant="h6"
          fontWeight={800}
          mb={1}
          sx={{ color: COLORS.text }}
        >
          About This Event
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: COLORS.muted, lineHeight: 1.6 }}
        >
          {eventData?.description || "No description available"}
        </Typography>
      </Box>

      {/* Live Interaction Note */}
      <Stack direction="row" spacing={1} alignItems="center" mt={2} mb={3}>
        <Circle sx={{ fontSize: 10, color: COLORS.red }} />
        <Typography variant="caption" sx={{ color: COLORS.muted }}>
          Includes interactive chat
        </Typography>
      </Stack>

      {/* Preview Video */}
      {eventData?.preview_video_url && (
        <Box mb={4}>
          <Typography
            variant="h6"
            fontWeight={800}
            mb={1}
            sx={{ color: COLORS.text }}
          >
            Preview Video
          </Typography>
          <Box
            component="iframe"
            width="100%"
            height="315"
            src={getEmbeddedVideoUrl(eventData.preview_video_url)}
            title="Preview Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`,
              background: "#000",
            }}
          />
        </Box>
      )}

      {/* Gallery */}
      {eventData?.gallery_images?.length > 0 && (
        <Box mb={4}>
          <Typography
            variant="h6"
            fontWeight={800}
            mb={1}
            sx={{ color: COLORS.text }}
          >
            Gallery
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.24)",
                borderRadius: 4,
              },
            }}
          >
            {eventData.gallery_images.map(
              (img: { id: number; url: string }) => (
                <Box
                  key={img.id}
                  component="img"
                  src={img.url}
                  alt={`Gallery image ${img.id}`}
                  onClick={() => setSelectedImage(img.url)}
                  sx={{
                    height: { xs: 120, sm: 140, md: 160 },
                    width: { xs: 180, sm: 210, md: 240 },
                    borderRadius: 2,
                    flexShrink: 0,
                    objectFit: "cover",
                    cursor: "pointer",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    border: `1px solid ${COLORS.border}`,
                    boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
                    "&:hover": {
                      transform: "scale(1.03)",
                      boxShadow: "0 18px 48px rgba(0,0,0,0.55)",
                    },
                  }}
                />
              )
            )}
          </Stack>

          {/* Image Lightbox */}
          {selectedImage && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 1300,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 2,
              }}
              onClick={() => setSelectedImage(null)}
            >
              <Box
                component="img"
                src={selectedImage}
                alt="Full view"
                sx={{
                  maxWidth: "92%",
                  maxHeight: "92%",
                  borderRadius: 3,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
                  objectFit: "contain",
                  border: `1px solid ${COLORS.border}`,
                }}
              />
            </Box>
          )}
        </Box>
      )}

      <GuestLiteModal
        open={isGuestLiteModalOpen}
        onClose={() => setIsGuestLiteModalOpen(false)}
      />

      {/* Booking Modal */}
      <BookingModal
        open={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        eventId={id ?? ""}
        title={eventData?.title ?? "No title available"}
        ticketPrice={eventData?.price ?? 0}
        userBalance={purchasedChips + earnedChips}
        startDateTime={eventData?.start_date_time}
        onBookingSuccess={handleBookingSuccess}
        venueSize={eventData?.venue_size ?? "Small"}
      />
    </Box>
  );
};

export default FeaturedEventDetail;
